/* MUI */
import { Box, Chip, Divider, TableCell, Typography } from '@mui/material';

/*  Shared  */
import {
    ApplicationBubbles,
    Sources,
    UserBubbles
} from 'application/Shared/Components/Avatars';
import { DateFormat } from 'application/Shared/Components/Core';
import { ListUserAccounts } from 'application/Shared/Components/ListUserAccounts';
import { ManagedTable } from 'application/Shared/Components/Table';
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { Helpers } from 'application/Shared/Helpers';

/* React */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const AccountStatus = ({ dormant }) => {
    const { t } = useTranslation();
    const type = dormant ? 'dormant' : 'active';
    return (
        <DarkTooltip title={dormant ? t('Tooltips.account.dormantStatus') : ''}>
            <Box>
                <Chip
                    label={t(`Common.${type}`)}
                    size="small"
                    sx={{
                        '& .MuiChip-label': {
                            color: `accountsReport.chipType.${type}.textColor`
                        },
                        backgroundColor: `accountsReport.chipType.${type}.backgroundColor`,
                        border: '1px solid',
                        borderColor: `accountsReport.chipType.${type}.borderColor`,
                        fontWeight: 600
                    }}
                />
            </Box>
        </DarkTooltip>
    );
};

const csvFields = (t) => [
    { key: 'username', label: t('Common.account') },
    {
        key: 'personal',
        label: t('Reports.AccountsInventory.personal')
    },
    {
        key: 'work',
        label: t('Reports.AccountsInventory.work')
    },
    {
        key: 'shared',
        label: t('Reports.AccountsInventory.shared')
    },
    {
        key: 'service',
        label: t('Reports.AccountsInventory.service')
    },
    { key: 'numberUsers', label: t('UsersAccountsAndDevices.numberOfUsers') },
    { key: 'users', label: t('Common.users') },
    { key: 'sources.LOGIN', label: t('Common.cloud_logins') },
    { key: 'sources.GOOGLE_SSO', label: t('Common.google_sso') },
    { key: 'sources.MICROSOFT_SSO', label: t('Common.microsoft_sso') },
    {
        key: 'sources.AZURE_SIGN_IN',
        label: t('Common.microsoftEntraID.logins')
    },
    { key: 'sources.GOOGLE_SIGN_IN', label: t('Common.google_logins') },
    { key: 'firstUsed', label: t('UsersAccountsAndDevices.discoveredDate') },
    { key: 'lastUsed', label: t('UsersAccountsAndDevices.lastTimeLogin') },
    { key: 'dormant', label: t('Common.dormantAccount') },
    { key: 'numberApps', label: t('UsersAccountsAndDevices.numberOfApps') },
    { key: 'apps', label: t('Common.appsAccessed') }
];

const getHeaders = ({ accountType, t }) => [
    {
        align: 'left',
        field: 'username',
        renderHeader: (
            <strong>{t(`Reports.AccountsInventory.${accountType}`)}</strong>
        ),
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'username',
        width: '15%'
    },
    {
        align: 'left',
        field: 'users',
        renderHeader: <strong>{t('Common.users')}</strong>,
        sortable: true,
        sortableFieldType: 'number',
        sortableKey: 'users',
        width: '15%'
    },
    {
        align: 'left',
        field: 'source',
        renderHeader: <strong>{t('Common.source')}</strong>,
        sortable: false,
        width: '15%'
    },
    {
        align: 'left',
        field: 'firstUsed',
        renderHeader: (
            <strong>{t('UsersAccountsAndDevices.discoveredDate')}</strong>
        ),
        sortable: true,
        sortableFieldType: 'date',
        sortableKey: 'firstUsed',
        width: '15%'
    },
    {
        align: 'left',
        field: 'dormant',
        renderHeader: <strong>{t('Common.accountStatus')}</strong>,
        sortable: false,
        width: '10%'
    },
    {
        align: 'left',
        field: 'lastUsed',
        renderHeader: (
            <strong>{t('UsersAccountsAndDevices.lastTimeLogin')}</strong>
        ),
        sortable: true,
        sortableFieldType: 'date',
        sortableKey: 'lastUsed',
        width: '15%'
    },
    {
        align: 'left',
        field: 'applications',
        renderHeader: <strong>{t('Common.appsAccessed')}</strong>,
        sortable: true,
        sortableFieldType: 'number',
        sortableKey: 'applications',
        width: '10%'
    }
];

const Subtitle = ({ subtitle }) => (
    <Box marginTop="64px">
        <Typography variant="h6" fontWeight={600} mt="16px">
            {subtitle}
        </Typography>
        <Box py="16px">
            <Divider
                sx={{
                    borderColor: 'border.divider',
                    borderRadius: '20px',
                    borderWidth: '4px',
                    opacity: 1
                }}
            />
        </Box>
    </Box>
);

const AccountTable = ({ accountType, filteredAccounts }) => {
    const { t } = useTranslation();

    const csvData = filteredAccounts.map((account) => {
        const {
            applications,
            dormant,
            firstUsed,
            lastUsed,
            personal,
            service,
            shared,
            sources,
            username,
            users,
            work
        } = account;

        const apps = applications
            .map((application) => application.name)
            .join(', ');

        const reducedUsers = users.map((user) => user.name).join(', ');
        return {
            apps,
            dormant,
            firstUsed,
            lastUsed,
            numberApps: applications.length,
            numberUsers: users.length,
            personal,
            service,
            shared,
            sources,
            username,
            users: reducedUsers,
            work
        };
    });

    const headers = useMemo(
        () => getHeaders({ accountType, t }),
        [accountType, t]
    );

    return (
        <Box>
            <Subtitle
                subtitle={t(`Reports.AccountsInventory.${accountType}Accounts`)}
            />
            <Box mb="32px" data-testid={`accountTableWrapper_${accountType}`}>
                <ManagedTable
                    csvData={csvData}
                    csvExport
                    csvExportFields={csvFields(t)}
                    csvExportFileName={`accountsOverviewReportTable_${accountType}_accounts`}
                    headers={headers}
                    renderBody={(row) => (
                        <>
                            <TableCell>
                                <ListUserAccounts
                                    accounts={[
                                        {
                                            personal: row.personal,
                                            service: row.service,
                                            shared: row.shared,
                                            username: row.username,
                                            work: row.work
                                        }
                                    ]}
                                />
                            </TableCell>
                            <TableCell>
                                <UserBubbles
                                    align="right"
                                    max={3}
                                    users={row.users}
                                    dataIsUser
                                />
                            </TableCell>
                            <TableCell>
                                <Sources
                                    sources={Helpers.getSourcesLoginForAccounts(
                                        row.sources
                                    )}
                                    max={3}
                                />
                            </TableCell>
                            <TableCell>
                                <DateFormat
                                    date={row.firstUsed}
                                    noTimeString
                                    noTz
                                />
                            </TableCell>
                            <TableCell>
                                <AccountStatus dormant={row.dormant} />
                            </TableCell>
                            <TableCell>
                                <DateFormat
                                    date={row.lastUsed}
                                    noTimeString
                                    noTz
                                />
                            </TableCell>
                            <TableCell>
                                <ApplicationBubbles
                                    max={3}
                                    applications={row.applications}
                                    tooltipPlacement="left"
                                />
                            </TableCell>
                        </>
                    )}
                    renderBodyKey="id"
                    renderFooter
                    rows={filteredAccounts}
                    rowsPerPage={-1}
                    sortDirection="ASC"
                    sortFieldType="string"
                    sortKey="application.name"
                />
            </Box>
        </Box>
    );
};

export const AccountsOverview = ({ accounts }) => {
    const shared = accounts.filter((acc) => acc.shared);
    const service = accounts.filter((acc) => acc.service);
    const personal = accounts.filter((acc) => acc.personal);
    const work = accounts.filter((acc) => acc.work);
    return (
        <Box mb="24px" pt="0" data-testid="accountsOverviewWrapper">
            {shared.length > 0 && (
                <AccountTable accountType="shared" filteredAccounts={shared} />
            )}
            {service.length > 0 && (
                <AccountTable
                    accountType="service"
                    filteredAccounts={service}
                />
            )}
            {work.length > 0 && (
                <AccountTable accountType="work" filteredAccounts={work} />
            )}
            {personal.length > 0 && (
                <AccountTable
                    accountType="personal"
                    filteredAccounts={personal}
                />
            )}
        </Box>
    );
};
